/** @jsxImportSource @emotion/react */
"use client";

import type { CSSObject } from "@emotion/react";
import { ClassNames } from "@emotion/react";
import { useLocale } from "@i18n/react";
import type { Locale } from "date-fns";
import { format } from "date-fns";
import { de, enUS, es, fr, it, pl, pt } from "date-fns/locale";
import { useMemo, useState } from "react";
import type { DayPickerLocale } from "react-day-picker";
import {
  DayFlag,
  DayPicker,
  getDefaultClassNames,
  SelectionState,
  UI,
} from "react-day-picker";

import type { ThemeType } from "../../../../../global/emotion";
import { numericPixels, radius } from "../../../../../global/emotion";
import { cn } from "../../../../utils/cn";
import { MonthSelector } from "./MonthSelector.emotion";

const cssMonthGrid = (): CSSObject => ({
  marginInline: "auto",
});

const cssMonthCaption: CSSObject = {
  display: "none",
};

const cssWeekday = (theme: ThemeType): CSSObject => ({
  width: numericPixels[48],
  height: numericPixels[48],
  fontSize: numericPixels[14],
  fontWeight: 700,
  lineHeight: numericPixels[18],
  color: theme.semanticColors.neutralsMedium,
  textAlign: "center",
  verticalAlign: "middle",
});

const cssDay: CSSObject = {
  width: numericPixels[48],
  height: numericPixels[48],
  padding: 0,
  borderRadius: radius.m,
};

const cssDayButton = (theme: ThemeType): CSSObject => ({
  position: "relative",
  zIndex: 1,
  width: numericPixels[48],
  height: numericPixels[48],
  borderRadius: radius.m,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: numericPixels[14],
  fontWeight: 700,
  lineHeight: numericPixels[18],
  color: "inherit",
  "&:disabled": {
    cursor: "not-allowed",
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.semanticColors.accentMedium}`,
    outlineOffset: -2,
  },
  ".rdp-today.rdp-hidden &": {
    background: "transparent",
    color: "inherit",
  },
  ".rdp-today &:hover:not(:disabled)": {
    background: "transparent",
    color: "white",
  },
  ".rdp-selected &:hover:not(:disabled)": {
    background: "transparent",
    color: theme.semanticColors.secondary,
  },
  "&:hover:not(:disabled)": {
    background: theme.semanticColors.neutralsVeryLow,
    borderRadius: radius.m,
    color: theme.semanticColors.primary,
  },
});

const cssSelected = (theme: ThemeType): CSSObject => ({
  background: theme.semanticColors.primary,
  color: theme.semanticColors.secondary,
  borderRadius: radius.m,
  "&.rdp-hidden": {
    background: "transparent",
  },
});

const cssToday = (theme: ThemeType): CSSObject => ({
  color: "white",
  position: "relative",
  background: "transparent",
  "::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "32px",
    height: "32px",
    borderRadius: radius.full,
    background: theme.semanticColors.accentMedium,
    zIndex: 0,
  },
});

const cssHighlighted = (theme: ThemeType): CSSObject => ({
  background: theme.semanticColors.accentLowest,
  color: theme.semanticColors.accentMedium,
  "&.rdp-hidden": {
    background: "transparent",
  },
  "&.rdp-today": {
    background: "transparent",
    color: "white",
  },
  "&.rdp-selected": {
    background: theme.semanticColors.primary,
    color: theme.semanticColors.secondary,
  },
});

const cssDisabled = (): CSSObject => ({
  opacity: 0.4,
  cursor: "not-allowed",
});

const localeCodeToDateFns: Record<string, Locale> = {
  de,
  en: enUS,
  "en-PH": enUS,
  es,
  fr,
  it,
  pl,
  pt,
};

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  options?: { displayHighlighted?: (date: Date) => boolean };
};

export const Calendar = ({ options, className, ...props }: CalendarProps) => {
  const [month, setMonth] = useState(props.defaultMonth ?? new Date());
  const defaultClassNames = getDefaultClassNames();
  const localeCode = useLocale();
  const dayPickerLocale =
    localeCodeToDateFns[localeCode] ?? (enUS as unknown as DayPickerLocale);

  const highlightMatcher = options?.displayHighlighted;
  const modifiers = useMemo(
    () => (highlightMatcher ? { highlighted: highlightMatcher } : {}),
    [highlightMatcher],
  );

  return (
    <ClassNames>
      {({ css, theme }) => {
        const t = theme as ThemeType;
        const dayButton = cssDayButton(t);

        return (
          <div className={className}>
            <MonthSelector currentMonth={month} onMonthChange={setMonth} />
            <DayPicker
              {...props}
              month={month}
              onMonthChange={setMonth}
              {...(["en", "en-PH"].includes(localeCode) && {
                formatters: {
                  formatWeekdayName: (date) =>
                    format(date, "EEEEE", { locale: enUS }),
                },
              })}
              locale={dayPickerLocale}
              modifiers={modifiers}
              modifiersClassNames={{ highlighted: css(cssHighlighted(t)) }}
              classNames={{
                [UI.MonthCaption]: css(cssMonthCaption),
                [UI.MonthGrid]: css(cssMonthGrid()),
                [UI.Weekday]: css(cssWeekday(t)),
                [UI.Day]: cn(css(cssDay), defaultClassNames[UI.Day]),
                [UI.DayButton]: cn(
                  css(dayButton),
                  defaultClassNames[UI.DayButton],
                ),
                [DayFlag.today]: cn(
                  css(cssToday(t)),
                  defaultClassNames[DayFlag.today],
                ),
                [DayFlag.disabled]: cn(
                  css(cssDisabled()),
                  defaultClassNames[DayFlag.disabled],
                ),
                [SelectionState.selected]: cn(
                  css(cssSelected(t)),
                  defaultClassNames[SelectionState.selected],
                ),
              }}
              components={{
                Nav: () => null,
              }}
            />
          </div>
        );
      }}
    </ClassNames>
  );
};
